import { useState, useMemo, useEffect, useCallback } from "react";
import { authFetch } from "../lib/authFetch";
import { usePortailRouter } from "../lib/router-context";
import { PartenaireVideoPlayer } from "../../src/components/partenaire-video-player";
import {
  Search, FileText, File, Film, Image as ImageIcon, FileType,
  Folder, FolderOpen, Download, Eye, ChevronRight, ChevronDown, X, BookOpen, Loader2,
} from "lucide-react";

// ── Types ──────────────────────────────────────────────────────────────────────
type CatWithCount = {
  id: string; nom: string; parentId: string | null;
  ordre: number; icone?: string; createdAt: string;
  docCount: number; hasUnread: boolean;
};
type RessourceDoc = {
  id: string; titre: string; description?: string; categorieId?: string;
  fichierUrl?: string; typeFichier?: string; visibilite: string;
  tailleFichier?: number; createdAt: string;
  signedUrl: string | null; isRead: boolean; isNew: boolean;
  mimeType?: string;
};
type RessourcesData = { categories: CatWithCount[]; documents: RessourceDoc[] };

// ── Helpers ────────────────────────────────────────────────────────────────────
function formatBytes(bytes?: number) {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
  return `${(bytes / 1024 / 1024).toFixed(1)} Mo`;
}

function formatDate(d?: string) {
  if (!d) return "";
  return new Date(d).toLocaleDateString("fr-FR");
}

function FileIcon({ type }: { type?: string }) {
  if (type === "pdf") return <FileText className="w-5 h-5 text-red-500" />;
  if (type === "word") return <File className="w-5 h-5 text-blue-500" />;
  if (type === "video") return <Film className="w-5 h-5 text-violet-500" />;
  if (type === "image") return <ImageIcon className="w-5 h-5 text-green-500" />;
  return <FileType className="w-5 h-5 text-muted-foreground" />;
}

// ── Category Node ──────────────────────────────────────────────────────────────
function CatNode({
  cat, children, selected, onSelect, depth = 0,
}: {
  cat: CatWithCount; children: CatWithCount[];
  selected: string | null; onSelect: (id: string) => void; depth?: number;
}) {
  const [open, setOpen] = useState(depth === 0);
  const hasChildren = children.length > 0;
  const active = selected === cat.id;

  return (
    <div>
      <button
        onClick={() => { setOpen(o => !o); onSelect(cat.id); }}
        className={`w-full flex items-center gap-1.5 py-1.5 px-2 rounded-lg text-sm transition-colors text-left ${
          active ? "bg-primary/10 text-primary font-medium" : "hover:bg-muted/50 text-foreground"
        }`}
        style={{ paddingLeft: `${8 + depth * 14}px` }}
        data-testid={`cat-ressource-${cat.id}`}
      >
        {hasChildren
          ? open ? <ChevronDown className="w-3 h-3 shrink-0 text-muted-foreground" /> : <ChevronRight className="w-3 h-3 shrink-0 text-muted-foreground" />
          : <span className="w-3 h-3 shrink-0" />}
        {depth === 0
          ? <Folder className="w-3.5 h-3.5 shrink-0 text-amber-500" />
          : <FolderOpen className="w-3.5 h-3.5 shrink-0 text-muted-foreground" />}
        <span className="flex-1 truncate">{cat.nom}</span>
        {cat.hasUnread && !active && (
          <span className="w-2 h-2 rounded-full bg-red-500 shrink-0" title="Documents non lus" />
        )}
        {cat.docCount > 0 && (
          <span className={`text-xs px-1 rounded-full font-medium shrink-0 ${active ? "bg-primary/20 text-primary" : "bg-muted text-muted-foreground"}`}>
            {cat.docCount}
          </span>
        )}
      </button>
      {open && hasChildren && children.map(child => (
        <CatNode key={child.id} cat={child} children={[]} selected={selected} onSelect={onSelect} depth={depth + 1} />
      ))}
    </div>
  );
}

// ── PDF Preview Panel ──────────────────────────────────────────────────────────
function PdfPreviewPanel({ url, titre, onClose }: { url: string; titre: string; onClose: () => void }) {
  return (
    <div className="flex flex-col h-full border-l bg-card">
      <div className="flex items-center justify-between p-3 border-b shrink-0">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-red-500" />
          <span className="text-sm font-medium truncate max-w-[200px]">{titre}</span>
        </div>
        <button onClick={onClose} className="p-1 rounded hover:bg-muted" data-testid="btn-close-preview">
          <X className="w-4 h-4 text-muted-foreground" />
        </button>
      </div>
      <iframe src={url} className="flex-1 w-full" title={titre} />
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────
export default function RessourcesPage() {
  const [search, setSearch] = useState("");
  const [selectedCatId, setSelectedCatId] = useState<string | null>(null);
  const [previewDoc, setPreviewDoc] = useState<RessourceDoc | null>(null);
  const [videoDoc, setVideoDoc] = useState<RessourceDoc | null>(null);
  const [videoError, setVideoError] = useState("");
  const [data, setData] = useState<RessourcesData | null>(null);
  const [loading, setLoading] = useState(true);
  const { refreshUnreadCount } = usePortailRouter();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authFetch("/api/portail/ressources");
      if (r.ok) {
        const d = await r.json();
        setData(d);
      }
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const categories: CatWithCount[] = data?.categories || [];
  const documents: RessourceDoc[] = data?.documents || [];

  const totalUnread = documents.filter(d => !d.isRead && d.isNew).length;

  const filteredDocs = useMemo(() => {
    let docs = documents;
    if (selectedCatId) {
      docs = docs.filter(d => d.categorieId === selectedCatId);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      docs = docs.filter(d => d.titre.toLowerCase().includes(q) || (d.description || "").toLowerCase().includes(q));
    }
    return docs;
  }, [documents, selectedCatId, search]);

  const rootCategories = categories.filter(c => !c.parentId);
  const childrenOf = (parentId: string) => categories.filter(c => c.parentId === parentId);

  const recordLecture = async (docId: string) => {
    try {
      await authFetch(`/api/portail/ressources/${docId}/lecture`, { method: "POST" });
      // Update local state to mark as read
      setData(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          documents: prev.documents.map(d => d.id === docId ? { ...d, isRead: true } : d),
          categories: prev.categories.map(cat => ({
            ...cat,
            hasUnread: prev.documents
              .filter(d => d.categorieId === cat.id)
              .some(d => d.id !== docId && !d.isRead),
          })),
        };
      });
      refreshUnreadCount();
    } catch {}
  };

  const handleDownload = async (doc: RessourceDoc) => {
    await recordLecture(doc.id);
    if (doc.signedUrl) window.open(doc.signedUrl, "_blank");
  };

  const handlePreview = async (doc: RessourceDoc) => {
    await recordLecture(doc.id);
    setPreviewDoc(doc);
  };

  const handleVideo = async (doc: RessourceDoc) => {
    setVideoDoc(null);
    setVideoError("");
    try {
      // Réutilise le filtre de visibilité serveur et renouvelle une URL expirée.
      const response = await authFetch("/api/portail/ressources");
      if (!response.ok) throw new Error("Lecture vidéo indisponible.");
      const fresh: RessourcesData = await response.json();
      setData(fresh);
      const visible = fresh.documents.find(d => d.id === doc.id);
      if (!visible?.signedUrl) throw new Error("Cette vidéo n'est plus disponible.");
      setPreviewDoc(null);
      setVideoDoc(visible);
      await recordLecture(doc.id);
    } catch (error) {
      setVideoError(error instanceof Error ? error.message : "Lecture vidéo indisponible.");
    }
  };

  return (
    <div className="flex h-full">
      <style>{`
        @media (max-width: 767px) {
          .partenaire-video-preview {
            position: fixed;
            left: 1rem;
            right: 1rem;
            top: 6rem;
            z-index: 50;
            max-height: 75vh;
            overflow-y: auto;
            box-shadow: 0 10px 30px rgb(0 0 0 / 20%);
          }
        }
      `}</style>
      {/* Zone principale */}
      <div className={`flex flex-col flex-1 min-w-0 overflow-hidden ${previewDoc ? "max-w-[60%]" : ""}`}>
        {/* Header */}
        <div className="flex flex-wrap items-center gap-4 px-4 sm:px-6 py-4 border-b bg-background shrink-0">
          <div className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-primary" />
            <h1 className="text-xl font-bold">Ressources</h1>
            {totalUnread > 0 && (
              <span
                className="inline-flex items-center px-2 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400 text-xs font-medium"
                data-testid="badge-unread-ressources"
              >
                {totalUnread} nouveau{totalUnread > 1 ? "x" : ""}
              </span>
            )}
          </div>
          <div className="flex-1 relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Rechercher un document..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-1.5 text-sm border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary/30"
              data-testid="input-search-ressources"
            />
          </div>
        </div>

        {/* Corps : catégories + liste */}
        <div className="flex flex-1 overflow-hidden">
          {/* Sidebar catégories */}
          <aside className="hidden md:block w-56 shrink-0 border-r p-2 overflow-y-auto bg-muted/20">
            <button
              onClick={() => setSelectedCatId(null)}
              className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm transition-colors ${!selectedCatId ? "bg-primary/10 text-primary font-medium" : "hover:bg-muted/50 text-foreground"}`}
              data-testid="cat-ressource-all"
            >
              <Folder className="w-3.5 h-3.5 text-muted-foreground" />
              <span className="flex-1 text-left">Toutes les ressources</span>
              <span className="text-xs bg-muted text-muted-foreground px-1 rounded">{documents.length}</span>
            </button>
            <div className="mt-1 space-y-0.5">
              {rootCategories.map(cat => (
                <CatNode
                  key={cat.id} cat={cat} children={childrenOf(cat.id)}
                  selected={selectedCatId} onSelect={setSelectedCatId}
                />
              ))}
            </div>
          </aside>

          {/* Liste documents */}
          <main className="flex-1 min-w-0 overflow-y-auto p-4">
            {videoError && <p role="alert" className="text-sm text-red-600 mb-3">{videoError}</p>}
            {videoDoc?.signedUrl && (
              <section aria-label="Lecteur vidéo" className="partenaire-video-preview mb-4 rounded-xl border bg-card p-3 min-w-0">
                <div className="flex justify-between gap-2 mb-3">
                  <h2 className="text-sm font-medium break-words">{videoDoc.titre}</h2>
                  <button aria-label="Fermer la vidéo" onClick={() => setVideoDoc(null)}><X className="w-4 h-4" /></button>
                </div>
                <PartenaireVideoPlayer key={videoDoc.signedUrl} url={videoDoc.signedUrl} title={videoDoc.titre} />
              </section>
            )}
            {loading ? (
              <div className="flex items-center justify-center h-32 text-muted-foreground gap-2">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span className="text-sm">Chargement...</span>
              </div>
            ) : filteredDocs.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-48 text-muted-foreground gap-2">
                <FileText className="w-10 h-10 opacity-20" />
                <span className="text-sm">
                  {search ? "Aucun résultat pour cette recherche." : "Aucun document disponible dans cette catégorie."}
                </span>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredDocs.map(doc => (
                  <div
                    key={doc.id}
                    className={`flex flex-wrap items-start gap-3 p-3 rounded-xl border bg-card hover:shadow-sm transition-all ${!doc.isRead ? "border-primary/20 bg-primary/[0.02]" : ""}`}
                    data-testid={`doc-ressource-${doc.id}`}
                  >
                    <div className="shrink-0 mt-0.5">
                      <FileIcon type={doc.typeFichier} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`font-medium text-sm break-words ${!doc.isRead ? "text-foreground" : "text-foreground/80"}`}>
                          {doc.titre}
                        </span>
                        {doc.isNew && !doc.isRead && (
                          <span
                            className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-400 font-semibold"
                            data-testid={`badge-new-${doc.id}`}
                          >
                            Nouveau
                          </span>
                        )}
                        {!doc.isRead && !doc.isNew && (
                          <span className="w-2 h-2 rounded-full bg-blue-500 shrink-0" title="Non lu" />
                        )}
                      </div>
                      {doc.description && (
                        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{doc.description}</p>
                      )}
                      <div className="flex flex-wrap items-center gap-3 mt-1.5">
                        <span className="text-xs text-muted-foreground">Ajouté le {formatDate(doc.createdAt)}</span>
                        {doc.tailleFichier && (
                          <span className="text-xs text-muted-foreground">{formatBytes(doc.tailleFichier)}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5 min-w-0">
                      {doc.mimeType?.startsWith("video/") && (
                        <button onClick={() => handleVideo(doc)} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs border hover:bg-muted/50"
                          data-testid={`btn-lire-${doc.id}`}>
                          <Film className="w-3.5 h-3.5" /> Lire
                        </button>
                      )}
                      {doc.typeFichier === "pdf" && doc.signedUrl && (
                        <button
                          onClick={() => handlePreview(doc)}
                          className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs border border-border hover:bg-muted/50 transition-colors text-muted-foreground"
                          data-testid={`btn-apercu-${doc.id}`}
                        >
                          <Eye className="w-3.5 h-3.5" />
                          Aperçu
                        </button>
                      )}
                      {doc.signedUrl ? (
                        <button
                          onClick={() => handleDownload(doc)}
                          className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs bg-primary text-white hover:bg-primary/90 transition-colors font-medium"
                          data-testid={`btn-download-${doc.id}`}
                        >
                          <Download className="w-3.5 h-3.5" />
                          Télécharger
                        </button>
                      ) : (
                        <span className="text-xs text-muted-foreground italic">Fichier indisponible</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </main>
        </div>
      </div>

      {/* Panneau aperçu PDF */}
      {previewDoc && previewDoc.signedUrl && (
        <div className="w-[40%] shrink-0">
          <PdfPreviewPanel
            url={previewDoc.signedUrl}
            titre={previewDoc.titre}
            onClose={() => setPreviewDoc(null)}
          />
        </div>
      )}
    </div>
  );
}
